import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { modulesAPI, lessonsAPI, testsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Button from '../components/UIKit/Button';
import ModuleCard from '../components/courses/ModuleCard';
import { 
  Save as SaveIcon, 
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import LessonSelector from '../components/courses/LessonSelector';
import { 
  Container,
  Box, 
  Typography, 
  Grid, 
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProfessions, selectAllProfessions, selectProfessionsLoading } from '../redux/slices/professionsSlice';
import { fetchSkills, selectAllSkills, selectSkillsLoading } from '../redux/slices/skillsSlice';

const ModuleEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation(['modules', 'courses', 'common']);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Redux селекторы для профессий и навыков
  const professions = useSelector(selectAllProfessions);
  const professionsLoading = useSelector(selectProfessionsLoading);
  const skills = useSelector(selectAllSkills);
  const skillsLoading = useSelector(selectSkillsLoading);

  const [lessonSelectorOpen, setLessonSelectorOpen] = useState(false);
  const [module, setModule] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [localModuleFields, setLocalModuleFields] = useState({});
  const [isDraft, setIsDraft] = useState(true);

  // Загружаем профессии и навыки при монтировании компонента
  useEffect(() => {
    if (professions.length === 0 && !professionsLoading) {
      dispatch(fetchProfessions({ all: true, isCommon: 1 }));
    }
    if (skills.length === 0 && !skillsLoading) {
      dispatch(fetchSkills({ all: true, isCommon: 1 }));
    }
  }, [dispatch, professions.length, professionsLoading, skills.length, skillsLoading]);

  // Загрузка модуля
  useEffect(() => {
    const loadModule = async () => {
      try {
        setLoading(true);
        const response = await modulesAPI.getById(id);
        const moduleData = response.data;
        
        setModule(moduleData);
        setIsDraft(moduleData.isDraft !== undefined ? moduleData.isDraft : true);
        setLocalModuleFields({
          'module-0-title': moduleData.title || '',
          'module-0-description': moduleData.description || '',
          'module-0-content': moduleData.content || '',
          'module-0-videoUrl': moduleData.videoUrl || '',
        });

        // Инициализируем уроки из загруженных данных
        if (moduleData.lessons) {
          setModule(prev => ({ ...prev, lessons: moduleData.lessons }));
        }
      } catch (error) {
        console.error('Ошибка при загрузке модуля:', error);
        toast.error(t('modulesPage.errors.loadError'));
        navigate('/modules');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadModule();
    }
  }, [id, navigate, t]);

  // Проверка прав на редактирование
  const canEdit = () => {
    if (!user || !module) return false;
    return user.userMeta?.roles?.includes('admin') || 
           user.userMeta?.roles?.includes('teacher') ||
           module.author?._id === user.id;
  };

  // Обработчики для ModuleCard
  const handleToggleExpanded = (moduleIndex, expanded) => {
    setIsExpanded(expanded);
  };

  const handleModuleFieldChange = (moduleIndex, e) => {
    const { name, value } = e.target;
    setLocalModuleFields(prev => ({
      ...prev,
      [`module-${moduleIndex}-${name}`]: value
    }));
    // Также обновляем состояние module для синхронизации
    setModule(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddLesson = (moduleIndex) => {
    const tempId = `temp-lesson-${Date.now()}-${Math.random()}`;
    setModule(prev => ({
      ...prev,
      lessons: [
        ...(prev.lessons || []),
        {
          _id: tempId, // Временный ID для React key
          title: '',
          content: '',
          duration: 15,
          type: 'text',
          contentType: 'standard',
          contentBlocks: [],
          resources: [],
          practiceExercises: [],
          tests: [], // Тесты теперь находятся внутри урока
        }
      ]
    }));
  };

  const handleRemoveLesson = (moduleIndex, lessonIndex) => {
    setModule(prev => ({
      ...prev,
      lessons: prev.lessons.filter((_, index) => index !== lessonIndex)
    }));
  };


  const handleOpenLessonSelector = (moduleIndex) => {
    setLessonSelectorOpen(true);
  };

  const handleSelectLessons = (selectedLessons) => {
    selectedLessons.forEach(lesson => {
      const isLessonAlreadyAdded = module.lessons?.some(
        existingLesson => existingLesson._id === lesson._id
      );
      
      if (!isLessonAlreadyAdded) {
        setModule(prev => ({
          ...prev,
          lessons: [...(prev.lessons || []), lesson]
        }));
      }
    });
    
    setLessonSelectorOpen(false);
    toast.success(t('moduleEdit.lessonsAdded', { count: selectedLessons.length }));
  };

  const handleLessonChange = (moduleIndex, action, data, lessonIndex) => {
    setModule(prev => {
      const newModule = { ...prev };
      if (!newModule.lessons) newModule.lessons = [];

      switch (action) {
        case 'update':
          newModule.lessons[lessonIndex] = data;
          break;
        case 'add':
          newModule.lessons.push(data);
          break;
        case 'addArray':
          newModule.lessons.push(...data);
          break;
        case 'remove':
          newModule.lessons.splice(lessonIndex, 1);
          break;
        default:
          break;
      }
      return newModule;
    });
  };


  const handleDraftToggle = (moduleIndex, isDraft) => {
    setIsDraft(isDraft);
    setModule(prev => ({
      ...prev,
      isDraft
    }));
  };

  // Обработка отправки формы
  const handleSubmit = async (e) => {
    e.preventDefault();

    const title = localModuleFields['module-0-title'] || module.title;
    if (!title.trim()) {
      toast.error(t('moduleEdit.titleRequired'));
      return;
    }

    try {
      setSaving(true);

      // Подготавливаем данные модуля для отправки
      const moduleData = {
        title,
        description: localModuleFields['module-0-description'] || module.description || '',
        content: localModuleFields['module-0-content'] || module.content || '',
        videoUrl: localModuleFields['module-0-videoUrl'] || module.videoUrl || '',
        isDraft: isDraft,
        lessons: module.lessons || [],
      };

      // Обновляем модуль
      await modulesAPI.update(module._id, moduleData);
      console.log(`✓ Модуль обновлен с ID: ${module._id}`);

      // Затем обновляем уроки модуля последовательно
      if (module.lessons && module.lessons.length > 0) {
        console.log(`📚 Обновляем ${module.lessons.length} уроков для модуля...`);

        for (let lessonIndex = 0; lessonIndex < module.lessons.length; lessonIndex++) {
          const lesson = module.lessons[lessonIndex];

          console.log(`  📖 Урок ${lessonIndex + 1}/${module.lessons.length}: "${lesson.title}"`);

          try {
            // Создаем базовую структуру урока
            const lessonData = {
              title: lesson.title || '',
              content: lesson.content || '',
              duration: Number(lesson.duration) || 15,
              type: lesson.type || 'text',
              contentType: lesson.contentType || 'standard',
              contentBlocks: lesson.contentBlocks || [],
              resources: lesson.resources || [],
              practiceExercises: lesson.practiceExercises || [],
              professions: lesson.professions || [],
              skills: lesson.skills || [],
              image: lesson.image || '',
              videoUrl: lesson.videoUrl || '',
              isDraft: lesson.isDraft === undefined ? false : lesson.isDraft,
              moduleId: module._id
            };

            // Если урок имеет _id и это не временный ID, сохраняем его
            if (lesson._id &&
                typeof lesson._id === 'string' &&
                !lesson._id.startsWith('temp-')) {
              lessonData._id = lesson._id;
            }

            let lessonId;
            const hasImageToUpload = lesson.imageFile || (lesson.imagePreview && !lesson.image);

            if (lesson._id && typeof lesson._id === 'string' && !lesson._id.startsWith('temp-')) {
              // Обновляем существующий урок
              lessonId = lesson._id;
              if (hasImageToUpload) {
                // Обновляем урок с изображением
                const formData = new FormData();

                const lessonDataForUpload = {
                  ...lessonData,
                  imagePreview: undefined,
                  imageFile: undefined
                };

                formData.append('data', JSON.stringify(lessonDataForUpload));

                // Добавляем изображение
                if (lesson.imageFile) {
                  formData.append('image', lesson.imageFile);
                } else if (lesson.imagePreview) {
                  const imageBlob = await fetch(lesson.imagePreview).then(r => r.blob());
                  formData.append('image', imageBlob, 'lesson-image.jpg');
                }

                await lessonsAPI.updateWithImage(lessonId, formData);
              } else {
                // Обновляем урок без изображения
                await lessonsAPI.update(lessonId, lessonData);
              }

              console.log(`    ✓ Урок обновлен с ID: ${lessonId}`);
            } else {
              // Создаем новый урок
              if (hasImageToUpload) {
                // Создаем урок с изображением
                const formData = new FormData();

                const lessonDataForUpload = {
                  ...lessonData,
                  imagePreview: undefined,
                  imageFile: undefined
                };

                formData.append('data', JSON.stringify(lessonDataForUpload));

                // Добавляем изображение
                if (lesson.imageFile) {
                  formData.append('image', lesson.imageFile);
                } else if (lesson.imagePreview) {
                  const imageBlob = await fetch(lesson.imagePreview).then(r => r.blob());
                  formData.append('image', imageBlob, 'lesson-image.jpg');
                }

                const lessonResponse = await lessonsAPI.createWithImage(formData);
                lessonId = lessonResponse.data.data?._id || lessonResponse.data._id || lessonResponse.data.lesson?._id;
              } else {
                // Создаем урок без изображения
                const lessonResponse = await lessonsAPI.create(lessonData);
                lessonId = lessonResponse.data.data?._id || lessonResponse.data._id || lessonResponse.data.lesson?._id;
              }

              if (!lessonId) {
                throw new Error('Не получен ID созданного урока');
              }

              console.log(`    ✓ Урок создан с ID: ${lessonId}`);
            }

            // Обновляем состояние урока после успешного сохранения
            setModule(prev => ({
              ...prev,
              lessons: prev.lessons.map((l, idx) =>
                idx === lessonIndex ? { ...l, _id: lessonId, imagePreview: null, imageFile: null } : l
              )
            }));

            // Сохраняем тесты урока
            const lessonTests = lesson.tests || [];
            if (lessonTests.length > 0) {
              console.log(`    🧪 Обновляем ${lessonTests.length} тестов для урока...`);

              for (let testIndex = 0; testIndex < lessonTests.length; testIndex++) {
                const test = lessonTests[testIndex];

                console.log(`      📝 Тест ${testIndex + 1}/${lessonTests.length}: "${test.title}"`);

                const testData = {
                  lessonId: lessonId,
                  title: test.title,
                  description: test.description || '',
                  timeLimit: Number(test.timeLimit) || 15,
                  passingScore: Number(test.passingScore) || 70,
                  attempts: Number(test.attempts) || 3,
                  questions: test.questions.map((q) => ({
                    ...q,
                    points: Number(q.points) || 1,
                    type: q.type || 'single-choice',
                  })),
                };

                // Убираем временный ID перед отправкой
                if (testData._id && testData._id.startsWith('temp-')) {
                  delete testData._id;
                }

                try {
                  if (test._id && !test._id.startsWith('temp-')) {
                    await testsAPI.update(test._id, testData);
                    console.log(`        ✓ Тест обновлен`);
                  } else {
                    await testsAPI.create(testData);
                    console.log(`        ✓ Тест создан`);
                  }
                } catch (error) {
                  console.error(`        ✗ Ошибка при обновлении теста "${test.title}":`, error);
                }
              }
            }
          } catch (error) {
            console.error(`    ✗ Ошибка при обновлении урока "${lesson.title}":`, error);
            toast.error(`Ошибка при обновлении урока "${lesson.title}"`);
          }
        }
      }

      toast.success(t('moduleEdit.updateSuccess'));
      navigate(`/modules/${module?.slug || id}`);
    } catch (error) {
      console.error('Ошибка при обновлении модуля:', error);
      toast.error(t('modulesPage.errors.loadError'));
    } finally {
      setSaving(false);
    }
  };

  // Обработка удаления модуля
  const handleDelete = async () => {
    if (!window.confirm(t('moduleEdit.deleteConfirm', { title: module?.title }))) {
      return;
    }

    try {
      // Используем реальный ObjectId модуля для удаления, а не id из URL
      await modulesAPI.delete(module._id);
      toast.success(t('moduleEdit.deleteSuccess'));
      navigate('/modules');
    } catch (error) {
      console.error('Ошибка при удалении модуля:', error);
      toast.error(t('modulesPage.errors.loadError'));
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!module) {
    return (
      <Container>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('modulesPage.noModules.title')}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/modules')}>
            {t('modulesPage.backToList')}
          </Button>
        </Box>
      </Container>
    );
  }

  if (!canEdit()) {
    return (
      <Container>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('moduleEdit.accessDenied')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('moduleEdit.noPermission')}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/modules')}>
            {t('modulesPage.backToList')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        {/* Заголовок */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(`/modules/${module?.slug || id}`)}
            >
              {t('moduleEdit.back')}
            </Button>
            <Typography variant="h3" component="h1" sx={{ flex: 1 }}>
              {t('moduleEdit.title')}
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            {t('moduleEdit.subtitle', { title: module?.title })}
          </Typography>
        </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
            {/* ModuleCard */}
                    <Grid item xs={12}>
              <ModuleCard
                moduleRef={module}
                moduleIndex={0}
                totalModules={1}
                isExpanded={isExpanded}
                localModuleFields={localModuleFields}
                user={user}
                isDraft={isDraft}
                showOrderField={false}
                onToggleExpanded={handleToggleExpanded}
                onModuleFieldChange={handleModuleFieldChange}
                onRemoveModule={() => {}}
                onAddLesson={handleAddLesson}
                onOpenLessonSelector={handleOpenLessonSelector}
                onLessonChange={handleLessonChange}
                onModuleOrderChange={() => {}}
                onDraftToggle={handleDraftToggle}
              />
            </Grid>

            {/* Кнопки действий */}
                    <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={saving}
                  onClick={handleSubmit}
                  fullWidth
                >
                  {saving ? t('moduleEdit.saving') : t('moduleEdit.saveChanges')}
                </Button>
                
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate(`/modules/${module?.slug || id}`)}
                  disabled={saving}
                  fullWidth
                >
                  {t('moduleEdit.cancel')}
                </Button>

                <Button
                  type="button"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  disabled={saving}
                  fullWidth
                >
                  {t('moduleEdit.deleteModule')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        {/* Диалог выбора уроков из библиотеки */}
        <LessonSelector
          open={lessonSelectorOpen}
          onClose={() => setLessonSelectorOpen(false)}
          onSelectLessons={handleSelectLessons}
        />
      </Box>
    </Container>
  );
};

export default ModuleEdit;
